import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack, Typography, Chip, Button, Divider, CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import GlobalDialog from "../../../components/mui/Dialogue/GlobalDialog";
import { getJobById, getFormTemplates } from "../../../redux/features/careerSlice";

// show a custom-field value the way a student sees it (arrays as a list, yes/no tidy)
const showVal = (v) => {
  if (Array.isArray(v)) return v.join(", ");
  if (v === "yes") return "Yes";
  if (v === "no") return "No";
  return String(v);
};

// Officer-side preview: renders a job exactly how a student would see it on the
// student portal (same fields, tags, and the custom "job preference" answers).
const StudentPreviewDialog = ({ jobId, open, setOpen }) => {
  const dispatch = useDispatch();
  const { formTemplates } = useSelector((s) => s.career);
  const [job, setJob] = useState(null);
  const [loadingJob, setLoadingJob] = useState(false);

  // on phones the preview goes full screen so the card isn't squeezed
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md")); // phones + small tablets

  useEffect(() => {
    if (!open || !jobId) return;
    setLoadingJob(true);
    dispatch(getFormTemplates());
    dispatch(getJobById(jobId)).then((res) => {
      setJob(res.payload?.data || null);
      setLoadingJob(false);
    });
  }, [open, jobId, dispatch]);

  // pair the template's field labels with the job's saved answers (metadata)
  const template =
    job && job.career_form_template_id
      ? (formTemplates || []).find((t) => t.career_form_template_id === job.career_form_template_id)
      : null;
  const meta = job?.metadata || {};
  const customFields = (template?.fields || [])
    .map((f) => ({ label: f.field_label, value: meta[f.field_key] }))
    .filter((cf) => {
      if (cf.value === undefined || cf.value === null || cf.value === "") return false;
      if (Array.isArray(cf.value) && cf.value.length === 0) return false;
      return true;
    });

  // status codes: 1 published (students can apply), 3 closed (read only), 0 draft (hidden)
  const status = Number(job?.job_status);
  const statusNote =
    status === 1 ? "Students can apply to this job." : status === 3 ? "Closed: students see it but cannot apply." : "Draft: students cannot see this job yet.";

  const tags = job
    ? [
        job.location,
        job.work_mode,
        job.salary && "₹" + job.salary,
        job.stipend && "Stipend: " + job.stipend,
        job.vacancies && job.vacancies + " openings",
      ].filter(Boolean)
    : [];

  return (
    <GlobalDialog
      title={"Student Preview"}
      open={open}
      setOpen={setOpen}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      closeButton
      content={
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is how this job appears to a student on the student portal.
          </Typography>

          {loadingJob || !job ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            // a card styled to resemble the student portal's job card
            <Box sx={{ border: "1px solid #e3e8ef", borderRadius: 2, p: 2.5, background: "#fff" }}>
              <Typography variant="h6" fontWeight={700}>{job.job_title}</Typography>
              <Typography variant="body2" sx={{ color: "#7b61ff", mb: 1 }}>
                {job.company_name || "Institution placement"}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 1 }}>
                {tags.map((t, i) => (
                  <Chip key={i} size="small" label={t} sx={{ background: "#eef2f7" }} />
                ))}
              </Stack>

              {job.description && (
                <Typography variant="body2" sx={{ my: 1 }}>{job.description}</Typography>
              )}
              {job.skills && (
                <Typography variant="body2" sx={{ my: 0.5 }}>
                  <b>Skills:</b> {job.skills}
                </Typography>
              )}
              {job.deadline && (
                <Typography variant="body2" sx={{ my: 0.5 }}>
                  <b>Apply by:</b> {job.deadline}
                </Typography>
              )}

              {/* the officer's custom answers (job preferences), as the student sees them */}
              {customFields.length > 0 && (
                <Box sx={{ mt: 1.5, p: 1.5, background: "#f5f7fa", borderRadius: 1.5 }}>
                  {customFields.map((cf, i) => (
                    <Typography key={i} variant="body2" sx={{ my: 0.25 }}>
                      <b>{cf.label}:</b> {showVal(cf.value)}
                    </Typography>
                  ))}
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Button size="small" variant="contained" disabled>
                  {status === 1 ? "Mark Interested" : "Closed"}
                </Button>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">{statusNote}</Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button variant="outlined" onClick={() => setOpen(false)}>Close</Button>
          </Box>
        </Box>
      }
    />
  );
};

export default StudentPreviewDialog;
